/**
 * Terse CDM constructors for the block catalog, mirroring StarterLayouts.cs's helpers so a palette
 * block and a starter layout build the same shapes. Catalog blocks are authored as CDM and
 * converted with fromCdm's blocksToPm, which keeps them identical to what a saved-and-reloaded
 * template would hold.
 */
import { normalizeMonikerKey } from '../cdm/monikerKey'
import {
  FieldType, HAlign,
  defaultCellFormat, defaultParagraphFormat, defaultRunFormat, defaultSourceLocation, defaultTableBorders,
  TableAlignment, TableLayout, RowHeightRule, TextBoxWrap,
  type CellFormat, type ConditionalBlock, type FormatterSpec, type DocBlock, type DocInline, type FieldRun, type HeadingBlock, type ImageBlock,
  type LoopBlock, type ParagraphBlock, type ParagraphFormat, type PlaceholderRun, type RunFormat, type TableBlock,
  type TableCell, type TableRow, type TextRun,
} from '../cdm/types'

export const SHADE = 'F1F5F9'

export const T = (text: string, fmt: Partial<RunFormat> = {}): TextRun =>
  ({ $type: 'run', text, format: { ...defaultRunFormat(), ...fmt } })

/** `{{name | formatter:args}}` — the parser's shape: rawName is the key alone, the pipeline in formatters. */
export const F = (name: string, formatter?: string, ...args: string[]): PlaceholderRun => ({
  $type: 'placeholder',
  rawName: name,
  normalizedKey: normalizeMonikerKey(name),
  formatters: formatter ? [{ name: formatter, args }] : null,
  format: defaultRunFormat(),
  source: defaultSourceLocation(),
})

/** A placeholder carrying a whole formatter pipeline. */
export const FP = (name: string, formatters: FormatterSpec[] | null): PlaceholderRun =>
  ({ ...F(name), formatters: formatters?.length ? formatters.map(f => ({ name: f.name, args: [...f.args] })) : null })

export const withFormat = (ph: PlaceholderRun, fmt: Partial<RunFormat>): PlaceholderRun => ({ ...ph, format: { ...ph.format, ...fmt } })

export const PageField = (fmt: Partial<RunFormat> = {}): FieldRun =>
  ({ $type: 'field', fieldType: FieldType.Page, staticText: '1', format: { ...defaultRunFormat(), ...fmt }, instruction: null })

export const P = (inlines: DocInline[] = [], fmt: Partial<ParagraphFormat> = {}): ParagraphBlock =>
  ({ $type: 'paragraph', format: { ...defaultParagraphFormat(), ...fmt }, inlines })

export const PA = (alignment: HAlign, inlines: DocInline[], spaceAfterPt = 0): ParagraphBlock => P(inlines, { alignment, spaceAfterPt })

export const H = (level: number, inlines: DocInline[], fmt: Partial<ParagraphFormat> = {}): HeadingBlock =>
  ({ $type: 'heading', level, format: { ...defaultParagraphFormat(), ...fmt }, inlines })

export const Gap = (pt = 12): ParagraphBlock => P([], { spaceAfterPt: pt })

export const C = (content: DocBlock[], fmt: Partial<CellFormat> = {}): TableCell => ({
  colSpan: 1, rowSpan: 1, isMergedContinuation: false, format: { ...defaultCellFormat(), ...fmt },
  content: content.length ? content : [P()], control: null, propertiesChange: null,
})

export const CS = (shade: string | null, content: DocBlock[]): TableCell => C(content, { shadingHex: shade, padding: 4 })

/** A shaded, bold header cell, as the line items table's header row uses. */
export const HeadCell = (text: string, align: HAlign = HAlign.Left) => CS(SHADE, [PA(align, [T(text, { bold: true })])])

export const FieldCell = (ph: DocInline, align: HAlign = HAlign.Left) => CS(null, [PA(align, [ph])])

export const Row = (cells: TableCell[], isHeader = false): TableRow =>
  ({ cells, isHeader, heightPt: null, heightRule: RowHeightRule.Auto, cantSplit: false, control: null, revision: null })

export const Table = (widths: number[], borders: boolean, rows: TableRow[]): TableBlock => ({
  $type: 'table',
  columnWidthsPt: widths,
  borders: { ...defaultTableBorders(), all: borders },
  rows,
  alignment: TableAlignment.Left,
  width: null,
  indentPt: null,
  layout: TableLayout.Auto,
  cellSpacingPt: null,
  propertiesChange: null,
})

export const Each = (collection: string, alias: string, body: DocBlock[]): LoopBlock => ({
  $type: 'loop', rawName: collection, normalizedKey: normalizeMonikerKey(collection), itemAlias: alias, body, source: defaultSourceLocation(),
})

export const If = (key: string, body: DocBlock[]): ConditionalBlock => ({
  $type: 'conditional', rawName: key, normalizedKey: normalizeMonikerKey(key), negate: false, body, elseBody: null, source: defaultSourceLocation(),
})

/** An image bound to a field, as the DOCX parser reads one whose alt text is `{{key}}`. */
export const DynamicImage = (rawName: string, widthPt: number, heightPt: number): ImageBlock => {
  const key = normalizeMonikerKey(rawName)
  return { $type: 'image', blobRef: `dyn:${key}`, widthPt, heightPt, altText: rawName, sourceMoniker: key, resolvedSource: null, wrap: TextBoxWrap.Inline, anchor: null, svgBlobRef: null }
}
